import { getEnvironment } from "./getEnvironment";

// Which GCP project to point observability links at, per environment.
// Local development uses staging so links resolve to a real project.
const PROJECT_BY_ENV: Record<ReturnType<typeof getEnvironment>, string> = {
  PRD: "pleevi-production",
  STG: "pleevi-staging",
  SBX: "pleevi-sandbox",
  LOCAL: "pleevi-staging",
};

export const getGcloudProject = (): string => PROJECT_BY_ENV[getEnvironment()];

// Default look-back window (ISO-8601 duration) for log/trace queries.
const LOOKBACK = "P7D";

const logsUrl = (query: string): string => {
  const project = getGcloudProject();
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(
    query,
  )};duration=${LOOKBACK}?project=${project}`;
};

type SpanFilters = {
  displayNames?: string[];
  services?: string[];
  attributes?: { key: string; value: string }[];
};

const tracesUrl = (spanFilters: SpanFilters): string => {
  const project = getGcloudProject();
  const query = {
    plotType: "HEATMAP",
    pointConnectionMethod: "GAP_DETECTION",
    targetAxis: "Y1",
    traceQuery: {
      resourceContainer: `projects/${project}/locations/global/traceScopes/_Default`,
      spanDataValue: "SPAN_DURATION",
      spanFilters: {
        apphubServices: [],
        apphubWorkloads: [],
        applicationIds: [],
        attributes: [],
        displayNames: [],
        isRootSpan: false,
        kinds: [],
        maxDuration: "",
        minDuration: "",
        services: [],
        status: [],
        ...spanFilters,
      },
    },
  };
  return `https://console.cloud.google.com/traces/explorer;query=${encodeURIComponent(
    JSON.stringify(query),
  )};duration=${LOOKBACK}?project=${project}`;
};

// The controller (Rust) emits logs/traces via OpenTelemetry, so site_id lives in
// otel resource attributes. Cloud Run services (Python) log site_id/task_id under
// jsonPayload. The site-wide views below combine both so you see all logs at once.
const controllerSiteFilter = (siteId: string) =>
  `otel.resource.attributes."site_id"="${siteId}"`;
const cloudRunSiteFilter = (siteId: string) =>
  `jsonPayload.site_id="${siteId}"`;

// --- Controller-specific (controller tab) ---

export const controllerLogsUrl = (siteId: string): string =>
  logsUrl(
    `otel.resource.attributes."service.name"="controller" AND ${controllerSiteFilter(
      siteId,
    )}`,
  );

export const controllerTracesUrl = (siteId: string): string =>
  tracesUrl({
    services: ["controller"],
    attributes: [{ key: "site_id", value: siteId }],
  });

// --- Site-wide (o11y tab): Cloud Run logs + controller logs together ---

export const siteLogsUrl = (siteId: string): string =>
  logsUrl(`${cloudRunSiteFilter(siteId)} OR ${controllerSiteFilter(siteId)}`);

export const siteTracesUrl = (siteId: string): string =>
  tracesUrl({ attributes: [{ key: "site_id", value: siteId }] });

// --- Task-specific (optimizations / forecasts) ---

export const taskLogsUrl = (taskId: string): string =>
  logsUrl(`jsonPayload.task_id="${taskId}"`);
